import {computed, inject, ref} from 'vue';
import {defineStore} from 'pinia';
import dayjs from 'dayjs';
import {customAlphabet} from 'nanoid';
import {isShopMember, isShopQR} from "@/common/utils";
import {apiError, standard} from "@/plugins/apiHook";
import * as dbManager from "./indexedDBManager";

const ALPHABET_KEY = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
const randomKey = customAlphabet(ALPHABET_KEY, 16)

export const useAppStore = defineStore('app', () => {

    let eventSource;
    const isSSEConnected = ref(false);
    const lastSSEPingAt = ref(null);

    const connect = async () => {
        await dbManager.initializeDB();

        eventSource = new EventSource('/sse');
        eventSource.onopen = onOpen;
        eventSource.onmessage = onMessage;
        eventSource.onerror = onError;
        // 서버에서 오는 ping 이벤트 수신 (event: ping, data: <timestamp>)
        eventSource.addEventListener('ping', (e) => {
            const ts = Number(e.data);
            if (!Number.isNaN(ts)) lastSSEPingAt.value = ts;
        });

        await Promise.all([
            loadDevices(),
            loadShops(),
            loadUsers(),
            loadRemocons(),
            // loadSchedules(),
            loadTasks(),
            loadPosts(),
            loadLogs(),
            loadWorks(),
            loadLinks(),
            // loadTelegramChatIds(),
        ]);
    };

    const disconnect = () => {
        if (eventSource) {
            console.log('[SSE] 🧹 SSE 연결 종료');
            eventSource.close();
            isSSEConnected.value = false;
        }
    };

    const onOpen = () => {
        isSSEConnected.value = true;
        console.log('[SSE] Connected');
    };

    const onMessage = event => {
        console.log('[SSE] Message:', event.data);
        const message = JSON.parse(event.data);

        (async () => {
            if (message.type === 'device')
                await loadDevices();
            else if (message.type === 'user')
                await loadUsers();
            else if (message.type === 'shop')
                await loadShops();
            else if (message.type === 'remocon')
                await loadRemocons();
            else if (message.type === 'task')
                await loadTasks();
            else if (message.type === 'post')
                await loadPosts();
            else if (message.type === 'log')
                await loadLogs();
            else if (message.type === 'work') {
                await loadWorks();
            }
        })();
    };

    const onError = error => {
        isSSEConnected.value = false;
        console.error('[SSE] Error:', error);
    };

    // WebSocket
    const socket = ref(null);
    const isSocketConnected = ref(false);
    const socketMessages = ref([]);

    const connectWs = () => {
        if (socket.value && socket.value.readyState === WebSocket.OPEN) return;

        const protocol = location.protocol === 'https:' ? 'wss' : 'ws';
        const url = `${protocol}://${location.host}/ws`;
        socket.value = new WebSocket(url);

        socket.value.onopen = onOpenWs;
        socket.value.onmessage = onMessageWs;
        socket.value.onclose = onCloseWs;
        socket.value.onerror = onErrorWs;
    };

    const disconnectWs = () => {
        if (!socket.value) return;
        socket.value.close();
        socket.value = null;
        isSocketConnected.value = false;
    };

    const sendWs = data => {
        if (socket.value && socket.value.readyState === WebSocket.OPEN) {
            socket.value.send(typeof data === 'string' ? data : JSON.stringify(data));
        } else {
            console.warn('[WebSocket] Not connected, cannot send');
        }
    };

    const onOpenWs = () => {
        isSocketConnected.value = true;
        console.log('[WebSocket] Connected');

        Promise.all([
            loadDevices(),
            loadShops(),
            loadUsers(),
            loadRemocons(),
            // loadSchedules(),
            loadTasks(),
            loadPosts(),
            // loadLogs(),
            // loadLinks(),
            // loadTelegramChatIds(),
        ]).then();
    };

    const onMessageWs = event => {
        console.log('[WebSocket] Message:', event.data);
        socketMessages.value.push(event.data);

        // 예: 메시지를 로그에 추가하는 경우
        // logs.value.push(JSON.parse(event.data));
    };

    const onCloseWs = () => {
        isSocketConnected.value = false;
        console.log('[WebSocket] Disconnected');
    };

    const onErrorWs = error => {
        console.error('[WebSocket] Error:', error);
    };

    // 데이터
    const axios = inject('axios');
    const network = inject('network');
    const devices = ref([]);
    const users = ref([]);
    const remocons = ref([]);
    const schedules = ref([]);
    const tasks = ref([]);
    const posts = ref([]);
    const logs = ref([]);
    const works = ref([]);
    const newWorkReceived = ref(false);
    const links = ref([]);
    const settings = ref([]);
    const telegramChatIds = ref([]);

    const loadSettings = async () => {
        try {
            const response = await axios.get(`/api/setting`);
            if (await apiError(response, standard)) return;

            settings.value = response.data.response;

            return response;

        } catch (error) {
            console.log(error);
        }
    };

    const loadDevices = async () => {
        try {
            const response = await axios.get(`/api/device`);
            if (await apiError(response, standard)) return;

            devices.value = response.data.response;

            return response;

        } catch (error) {
            console.log(error);
        }
    };

    const loadUsers = async () => {
        try {
            const response = await axios.get('/api/user');
            if (await apiError(response, standard)) return;

            users.value = response.data.response;

            return response;

        } catch (error) {
            console.log(error);
        }
    };

    const loadRemocons = async () => {
        try {
            const response = await axios.get(`/api/remocon`);
            if (await apiError(response, standard)) return;

            remocons.value = response.data.response.map(remocon => ({
                ...remocon,
                buttons: remocon.buttons,
            }));

            return response;

        } catch (error) {
            console.log(error);
        }
    };

    const loadSchedules = async () => {
        try {
            const response = await axios.get(`/api/schedule`);
            if (!response || 200 !== response.status || 0 !== response.data.code) return;

            schedules.value = response.data.response;

            return response;

        } catch (error) {
            console.log(error);
        }
    };

    const loadTasks = async () => {
        try {
            // 1) IndexedDB에서 복구
            const storedTasks = await dbManager.loadTasks();
            let lastUpdateTime = await dbManager.getUpdateTime('tasks');

            // updateTimes가 비어있어도 저장된 데이터로부터 복구하여 ut 계산
            if (!lastUpdateTime && 0 < storedTasks.length) {
                lastUpdateTime = storedTasks.reduce((acc, cur) => {
                    const [startedAt, finishedAt, closedAt, canceledAt, createdAt] = Array.isArray(cur.data) ? cur.data : [];
                    const lastUpdatedAt = [startedAt, finishedAt, closedAt, canceledAt, createdAt]
                    .filter(Boolean).reduce((acc, cur) => acc < cur ? cur : acc, null);
                return acc < lastUpdatedAt ? lastUpdatedAt : acc
                }, null);
            }

            if (lastUpdateTime) {
                try {
                    const utParam = dayjs(lastUpdateTime).isValid() 
                        ? dayjs(lastUpdateTime).format('YYYY-MM-DD HH:mm:ss.SSS') 
                        : lastUpdateTime;
                    const url = `/api/task?ut=${encodeURIComponent(utParam)}`;
                    const response = await axios.get(url);
                    if (await apiError(response, standard)) return;

                    const updatedTasks = response.data.response.filter(task => {
                        if (!task.device || !task.button) {
                            console.warn('[APP STORE] Task에 device 또는 button이 null입니다:', task);
                            return false;
                        }
                        return true;
                    });

                    const merged = [
                        ...storedTasks.filter(task => !updatedTasks.some(updated => updated.id === task.id)),
                        ...updatedTasks,
                    ].sort((l, r) => l.createdAt < r.createdAt);

                    await dbManager.saveTasks(merged);
                    tasks.value = merged;
                    return response;

                } catch (error) {
                    console.warn('[APP STORE] tasks 증분 업데이트 실패, IndexedDB 데이터 사용:', error);
                    tasks.value = storedTasks;
                    return storedTasks;
                }
            }

            // 2) 초기 전체 로드
            const response = await axios.get('/api/task');
            if (await apiError(response, standard)) return;

            const allTasks = response.data.response.filter(task => {
                if (!task.device || !task.button) {
                    console.warn('[APP STORE] Task에 device 또는 button이 null입니다:', task);
                    return false;
                }
                return true;
            }).sort((l, r) => l.createdAt < r.createdAt);

            await dbManager.saveTasks(allTasks);
            tasks.value = allTasks;

            return response;

        } catch (error) {
            console.log(error);
        }
    }

    const loadPosts = async () => {
        try {
            const response = await axios.get('/api/post');
            if (await apiError(response, standard)) return;

            posts.value = response.data.response.map(post => ({
                ...post,
                attachments: post.attachments.map(attachment => ({
                    ...attachment,
                    // uuid: uuid.v1(),
                    src: `/${attachment.path}`,
                })),
            }));

            return response;

        } catch (error) {
            console.log(error);
        }
    };

    const loadLogs = async (period = 'week') => {
        try {
            // IndexedDB에서 저장된 데이터 복구 시도
            const storedLogs = await dbManager.loadLogs();
            const lastUpdateTime = await dbManager.getUpdateTime('logs');

            // 저장된 데이터가 있고 업데이트 시간이 있으면 증분 업데이트 시도
            if (0 < storedLogs.length && lastUpdateTime) {
                try {
                    const utParam = dayjs(lastUpdateTime).isValid()
                        ? dayjs(lastUpdateTime).format('YYYY-MM-DD HH:mm:ss.SSS')
                        : lastUpdateTime;
                    const response = await axios.get(`/api/log?ut=${utParam}&period=${period}`);

                    if (response.data.code === 0 && response.data.response.length > 0) {
                        const newLogs = response.data.response;
                        const mergedLogs = [...storedLogs, ...newLogs];

                        await dbManager.cleanupOldLogs();
                        await dbManager.saveLogs(mergedLogs);

                        logs.value = mergedLogs;
                        return mergedLogs;
                    }
                } catch (error) {
                    console.warn('[APP STORE] 증분 업데이트 실패:', error);
                }

                // 증분 업데이트 실패 시 저장된 데이터 사용
                logs.value = storedLogs;
                return storedLogs;
            }

            // 전체 데이터 로드
            const [lastLog] = logs.value.slice(-1);
            const updateTime = lastLog
                ? dayjs(lastLog.createdAt).format('YYYY-MM-DD HH:mm:ss.SSS')
                : dayjs().subtract(1, 'week').format('YYYY-MM-DD HH:mm:ss.SSS');

            const response = await axios.get(`/api/log?ut=${updateTime}&period=${period}`);

            if (response.data.code === 0) {
                const newLogs = response.data.response;

                await dbManager.cleanupOldLogs();
                await dbManager.saveLogs(newLogs);

                logs.value = newLogs;
                return newLogs;
            }

            return [];
        } catch (error) {
            console.error('[APP STORE] 로그 로드 실패:', error);
            return [];
        }
    };

    const loadWorks = async () => {
        try {
            // 1) IndexedDB에서 복구 후 증분 시도
            const storedWorks = await dbManager.loadWorks();
            let lastUpdateTime = await dbManager.getUpdateTime('works');

            // updateTimes가 비어있어도 저장된 데이터의 createdAt 최대값으로 ut 복구
            if (!lastUpdateTime && 0 < storedWorks.length) {
                lastUpdateTime = storedWorks.reduce((acc, cur) => (!acc || (cur.createdAt && cur.createdAt > acc)) ? cur.createdAt : acc, null);
            }

            if (0 < storedWorks.length && lastUpdateTime) {
                try {
                    const url = `/api/work?ut=${encodeURIComponent(lastUpdateTime)}`;
                    const response = await axios.get(url);
                    if (await apiError(response, standard)) return;

                    const newWorks = response.data.response.map(work => ({
                        ...work,
                        shopName: work.Shop?.name || '알 수 없는 매장',
                        executorName: work.executor,
                        actionText: work.actionText,
                        executedAt: new Date(work.createdAt).toLocaleString('ko-KR'),
                        profileImage: work.User?.profileImage || null,
                    }));

                    const merged = [
                        ...storedWorks.filter(w => !newWorks.some(n => n.id === w.id)),
                        ...newWorks,
                    ];

                    await dbManager.saveWorks(merged);
                    works.value = merged;
                    return response;

                } catch (error) {
                    console.warn('[APP STORE] works 증분 업데이트 실패, IndexedDB 데이터 사용:', error);
                    works.value = storedWorks;
                    return storedWorks;
                }
            }

            // 2) 초기 전체 로드
            const response = await axios.get('/api/work');
            if (await apiError(response, standard)) return;

            const initialWorks = response.data.response.map(work => ({
                ...work,
                shopName: work.Shop?.name || '알 수 없는 매장',
                executorName: work.executor,
                actionText: work.actionText,
                executedAt: new Date(work.createdAt).toLocaleString('ko-KR'),
                profileImage: work.User?.profileImage || null,
            }));

            await dbManager.saveWorks(initialWorks);
            works.value = initialWorks;

            return response;

        } catch (error) {
            console.error('Error loading works:', error);
        }
    };

    const setNewWorkReceived = (value) => {
        newWorkReceived.value = value;
    };

    const loadLinks = async () => {
        try {
            const response = await axios.get(`/api/setting/links`);
            if (await apiError(response, standard)) return;

            links.value = response.data.response;

            return response;

        } catch (error) {
            console.log(error);
        }
    };

    const loadTelegramChatIds = async () => {
        try {
            const response = await axios.get(`/api/telegram/user`);
            if (await apiError(response, standard)) return;

            telegramChatIds.value = response.data.response
                .map(item => ({
                    display: item.title
                        ? `[${item.id}] ${item.title}`
                        : `[${item.id}] ${[item.last_name, item.first_name, item.username && `(${item.username})`, ].filter(v => v).join('')}`,
                    ...item,
                }));

            return response;

        } catch (error) {
            console.log(error);
        }
    };

    ////////////////////
    // Dashboard      //
    ////////////////////

    const controlAction = async payload => {
        try {
            const response = await axios.post(`/api/work`, payload);
            // Run the hook for side-effects (like logout), but still return the response
            // so the caller can handle logical errors.
            await apiError(response, standard);
            return response;
        } catch (error) {
            // For network errors, re-throw the error so the caller's catch block is executed.
            console.error("controlAction failed:", error);
            throw error;
        }
    }


    ////////////////////
    // Shop           //
    ////////////////////

    const _shops = ref([]);
    const shops = computed(() => {
        if (!_shops.value.length) return [];

        return _shops.value.map(shop => {
            const shopWorks = works.value.filter(work => work.ShopId === shop.id);
            const lastWork = shopWorks.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
            return {
                ...shop,
                lastWork: lastWork || null,
            };
        });
    });

    const loadShops = async () => {
        try {
            const preservedShops = _shops.value;
            // 마지막 shop의 updatedAt 또는 createdAt을 기억하여 증분 업데이트
            const updateTime = preservedShops.reduce((acc, cur) => acc < cur.updatedAt ? cur.updatedAt : acc, null);

            const url = updateTime 
                ? `/api/shop?ut=${encodeURIComponent(updateTime)}`
                : '/api/shop';
                
            console.log('Loading shops with update time:', updateTime);
            const response = await axios.get(url);
            if (await apiError(response, standard)) return;

            // 변경된 매장들 처리
            const updatedShops = response.data.response;

            console.log(`[APP STORE] Loaded ${updatedShops.length} shops, ${preservedShops.length} preserved`);

            _shops.value = [
                // 변경되지 않은 매장 추가
                ...preservedShops.filter(shop => !updatedShops.some(updated => updated.id === shop.id)),
                // 변경되거나 새로운 매장 추가
                ...updatedShops
            ]
                .map(shop => ({
                    ...shop,
                    nameWithId: `${shop.id}.${shop.name}`,
                    Users: shop.Users
                        .map(user => ({
                            ...user,
                            icon: isShopQR(user.account) ? 'mdi-qrcode'
                                : user.manager ? 'mdi-account-star'
                                    : 'mdi-account',
                            title: isShopMember(user.account)
                                ? user.name || user.account
                                : user.account,
                            subtitle: `생성일: ${dayjs(user.createdAt).format('YYYY-MM-DD HH:mm:ss')}`,
                            url: isShopQR(user.account) && generateQRUrl(user),
                        }))
                        .sort((a, b) => {
                            const qrA = isShopQR(a.account);
                            const qrB = isShopQR(b.account);

                            // 1. QR 코드 사용자 우선
                            if (qrA && !qrB) return -1;
                            else if (!qrA && qrB) return 1;

                            // 2. manager 우선
                            if (a.manager && !b.manager) return -1;
                            else if (!a.manager && b.manager) return 1;

                            // 3. 등록일 순 (오래된 것부터)
                            return new Date(a.createdAt) - new Date(b.createdAt);
                        }),
                }));

            return response;

        } catch (error) {
            console.log(error);
        }
    };

    const registerToken = async (shopId) => {
        try {
            const akey = randomKey();
            const response = await axios.post('/api/auth/registerToken', {
                shopId: shopId,
                akey
            });
            if (await apiError(response, standard)) return;

            return response.data.response;

        } catch (error) {
            console.log(error);
        }
    };

    const deactivateUser = async (account) => {
        try {
            const response = await axios.put('/api/user/deactivate', {
                account: account
            });
            if (await apiError(response, standard)) return;

            return response.data.response;

        } catch (error) {
            console.log(error);
        }
    };

    const createUser = async (userData) => {
        try {
            const response = await axios.post('/api/auth/createUser', userData);
            if (await apiError(response, standard)) return;

            return response.data.response;

        } catch (error) {
            console.log(error);
        }
    };

    const activateUser = async (userId) => {
        try {
            const response = await axios.put('/api/user/activate', {
                userId: userId
            });
            if (await apiError(response, standard)) return;

            return response.data.response;

        } catch (error) {
            console.log(error);
        }
    };


    ////////////////////
    // Log            //
    ////////////////////

    // const logs = ref([]);
    //
    // const loadLogs = async params => {
    //     const response = await axios.get(`/api/log?${params}`);
    //     if (await apiError(response, standard)) return;
    //
    //     logs.value = response.data.response;
    // };


    ////////////////////
    // Etc            //
    ////////////////////

    const clear = () => {
        devices.value = [];
        remocons.value = [];
        schedules.value = [];
        tasks.value = [];
        shops.value = [];
        posts.value = [];
        logs.value = [];
        works.value = [];
        links.value = [];
    };

    const generateQRUrl = user => {
        const token = `${user.account}.${user.accessKey}`;

        const currentUrl = new URL(`${window.location.origin}/login/qr`);
        currentUrl.searchParams.set('t', token);
        return currentUrl.toString();
    };

    return {
        connect, disconnect,
        connectWs, disconnectWs,
        isSSEConnected, lastSSEPingAt,
        devices, loadDevices,
        users, loadUsers,
        remocons, loadRemocons,
        schedules, loadSchedules,
        tasks, loadTasks,
        controlAction,
        shops, loadShops,
        registerToken,
        deactivateUser,
        createUser,
        activateUser,
        posts, loadPosts,
        logs, loadLogs,
        works, loadWorks, setNewWorkReceived,
        links, loadLinks,
        settings, loadSettings,
        telegramChatIds, loadTelegramChatIds,
        clear,
    };
});
